/**
 * PaymentHistory Component
 * Shared component for displaying user payment history with infinite scroll.
 * Used by both Student and THPT apps.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Calendar,
  CreditCard,
  CheckCircle2,
  Clock,
  Receipt,
  AlertCircle,
  Download,
  Loader2,
  RefreshCw,
  Tag,
} from 'lucide-react';
import { Button } from '@vsat/shared/components';
import { Badge } from '@vsat/shared/components';
import { Card, CardContent } from '@vsat/shared/components';
import { Skeleton } from '@vsat/shared/components';
import { toast } from 'sonner';
import { apiService, type PaymentTransaction, type PaymentListResponse } from '@vsat/shared/lib';

interface PaymentHistoryProps {
  pageSize?: number;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof CheckCircle2 }> = {
  COMPLETED: {
    label: 'Hoàn thành',
    color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    icon: CheckCircle2,
  },
  PENDING: {
    label: 'Đang xử lý',
    color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    icon: Clock,
  },
  FAILED: {
    label: 'Thất bại',
    color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    icon: AlertCircle,
  },
  REFUNDED: {
    label: 'Hoàn tiền',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    icon: RefreshCw,
  },
  EXPIRED: {
    label: 'Hết hạn',
    color: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
    icon: Clock,
  },
};

const ICON_BG: Record<string, string> = {
  COMPLETED: 'bg-green-100 dark:bg-green-900/30',
  PENDING: 'bg-yellow-100 dark:bg-yellow-900/30',
  FAILED: 'bg-red-100 dark:bg-red-900/30',
  REFUNDED: 'bg-blue-100 dark:bg-blue-900/30',
  EXPIRED: 'bg-gray-100 dark:bg-gray-900/30',
};

const ICON_COLOR: Record<string, string> = {
  COMPLETED: 'text-green-600 dark:text-green-400',
  PENDING: 'text-yellow-600 dark:text-yellow-400',
  FAILED: 'text-red-600 dark:text-red-400',
  REFUNDED: 'text-blue-600 dark:text-blue-400',
  EXPIRED: 'text-gray-600 dark:text-gray-400',
};

function formatCurrency(amount: number) {
  if (amount === 0) return 'Miễn phí';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('vi-VN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function PaymentCardSkeleton() {
  return (
    <Card>
      <CardContent className='p-6'>
        <div className='flex items-start gap-4'>
          <Skeleton className='w-10 h-10 rounded-lg' />
          <div className='flex-1 space-y-2'>
            <Skeleton className='h-5 w-48' />
            <Skeleton className='h-4 w-64' />
            <Skeleton className='h-4 w-40' />
          </div>
          <Skeleton className='h-6 w-24' />
        </div>
      </CardContent>
    </Card>
  );
}

export function PaymentHistory({ pageSize = 10 }: PaymentHistoryProps) {
  const [payments, setPayments] = useState<PaymentTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);

  const hasMore = payments.length < total;

  const fetchPayments = useCallback(
    async (offset: number, isLoadMore: boolean) => {
      if (isLoadMore && loadingMoreRef.current) return;
      try {
        if (isLoadMore) {
          loadingMoreRef.current = true;
          setLoadingMore(true);
        } else {
          setLoading(true);
          setError(null);
        }

        const response: PaymentListResponse = await apiService.getMyPayments({
          limit: pageSize,
          offset,
        });

        if (isLoadMore) {
          setPayments(prev => [...prev, ...response.data]);
        } else {
          setPayments(response.data);
        }
        setTotal(response.total);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Không thể tải lịch sử thanh toán';
        setError(message);
        if (isLoadMore) {
          toast.error('Không thể tải thêm giao dịch');
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
        loadingMoreRef.current = false;
      }
    },
    [pageSize]
  );

  useEffect(() => {
    fetchPayments(0, false);
  }, [fetchPayments]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loadingMore) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          fetchPayments(payments.length, true);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, payments.length, fetchPayments]);

  // SEO
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Lịch sử thanh toán | Thi Trên Máy Tính';

    let metaRobots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const hadExistingMeta = !!metaRobots;
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      metaRobots.content = 'noindex, nofollow';
      document.head.appendChild(metaRobots);
    }

    return () => {
      document.title = prevTitle;
      if (!hadExistingMeta && metaRobots) {
        metaRobots.remove();
      }
    };
  }, []);

  const getTitle = (payment: PaymentTransaction) => {
    return payment.examTitle || payment.description || `Giao dịch ${payment.orderId}`;
  };

  const completedPayments = payments.filter(p => p.status === 'COMPLETED');
  const totalSpent = completedPayments.reduce((sum, p) => sum + p.amount, 0);

  // Loading state
  if (loading) {
    return (
      <div className='space-y-6'>
        <div>
          <h3 className='text-lg font-medium text-foreground'>Lịch sử thanh toán</h3>
          <p className='text-sm text-muted-foreground mt-1'>
            Quản lý các giao dịch và hóa đơn của bạn
          </p>
        </div>
        <div className='space-y-4'>
          {Array.from({ length: 3 }).map((_, i) => (
            <PaymentCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error && payments.length === 0) {
    return (
      <div className='space-y-6'>
        <div>
          <h3 className='text-lg font-medium text-foreground'>Lịch sử thanh toán</h3>
          <p className='text-sm text-muted-foreground mt-1'>
            Quản lý các giao dịch và hóa đơn của bạn
          </p>
        </div>
        <Card>
          <CardContent className='p-8 text-center'>
            <AlertCircle className='w-12 h-12 text-red-400 mx-auto mb-4' />
            <h3 className='text-lg font-medium text-foreground mb-2'>Lỗi tải dữ liệu</h3>
            <p className='text-muted-foreground mb-4'>{error}</p>
            <Button variant='outline' onClick={() => fetchPayments(0, false)}>
              Thử lại
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div>
        <h3 className='text-lg font-medium text-foreground'>Lịch sử thanh toán</h3>
        <p className='text-sm text-muted-foreground mt-1'>
          Quản lý các giao dịch và hóa đơn của bạn
        </p>
      </div>

      {/* Payment List */}
      <div className='space-y-4'>
        {payments.length === 0 ? (
          <Card>
            <CardContent className='p-8 text-center'>
              <Receipt className='w-12 h-12 text-muted-foreground mx-auto mb-4' />
              <h3 className='text-lg font-medium text-foreground mb-2'>Chưa có giao dịch nào</h3>
              <p className='text-muted-foreground'>Các giao dịch thanh toán sẽ hiển thị tại đây</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {payments.map(payment => {
              const config = STATUS_CONFIG[payment.status] || STATUS_CONFIG.PENDING;
              const StatusIcon = config.icon;

              return (
                <article key={payment.id}>
                  <Card className='hover:shadow-md transition-shadow'>
                    <CardContent className='p-6'>
                      <div className='flex items-start justify-between gap-4'>
                        <div className='flex items-start gap-4 flex-1'>
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center ${ICON_BG[payment.status] || ICON_BG.PENDING}`}
                          >
                            <CreditCard
                              className={`w-5 h-5 ${ICON_COLOR[payment.status] || ICON_COLOR.PENDING}`}
                            />
                          </div>

                          <div className='flex-1 min-w-0'>
                            <div className='flex items-center gap-2 mb-2 flex-wrap'>
                              <h4 className='font-medium text-foreground'>{getTitle(payment)}</h4>
                              <Badge className={`${config.color} border-0`}>
                                <StatusIcon className='w-3 h-3 mr-1' />
                                {config.label}
                              </Badge>
                            </div>

                            <div className='space-y-1 text-sm text-muted-foreground'>
                              <div className='flex items-center gap-4 flex-wrap'>
                                <span>Mã GD: {payment.orderId}</span>
                                <span>•</span>
                                <span>{payment.paymentMethod}</span>
                              </div>
                              <div className='flex items-center gap-1 flex-wrap'>
                                <Calendar className='w-4 h-4' />
                                <time dateTime={payment.paidAt || payment.createdAt}>
                                  {formatDate(payment.paidAt || payment.createdAt)}
                                </time>
                                {payment.expiresAt && payment.status === 'PENDING' && (
                                  <>
                                    <span className='mx-2'>•</span>
                                    <span>
                                      Hết hạn QR:{' '}
                                      <time dateTime={payment.expiresAt}>
                                        {formatDate(payment.expiresAt)}
                                      </time>
                                    </span>
                                  </>
                                )}
                              </div>
                              {payment.discountAmount && payment.discountAmount > 0 && (
                                <div className='flex items-center gap-1 text-green-600 dark:text-green-400'>
                                  <Tag className='w-3.5 h-3.5' />
                                  <span>
                                    Giảm {formatCurrency(payment.discountAmount)}
                                    {payment.discountCode && ` (${payment.discountCode})`}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className='text-right shrink-0'>
                          {payment.originalAmount && payment.originalAmount > payment.amount && (
                            <div className='text-sm text-muted-foreground line-through'>
                              {formatCurrency(payment.originalAmount)}
                            </div>
                          )}
                          <div className='text-lg font-semibold text-foreground mb-2'>
                            {formatCurrency(payment.amount)}
                          </div>
                          {payment.status === 'COMPLETED' && (
                            <Button
                              variant='outline'
                              size='sm'
                              onClick={() => toast.info('Tính năng tải hóa đơn sẽ sớm ra mắt')}
                              className='gap-2'
                            >
                              <Download className='w-4 h-4' />
                              Hóa đơn
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </article>
              );
            })}

            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className='h-4' />
            {loadingMore && (
              <div className='flex items-center justify-center py-4'>
                <Loader2 className='w-5 h-5 animate-spin text-muted-foreground mr-2' />
                <span className='text-sm text-muted-foreground'>Đang tải thêm...</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Summary */}
      {payments.length > 0 && (
        <Card className='bg-gray-50 dark:bg-gray-800/50'>
          <CardContent className='p-6'>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4 text-center'>
              <div>
                <div className='text-2xl font-bold text-foreground'>{total}</div>
                <div className='text-sm text-muted-foreground'>Tổng giao dịch</div>
              </div>
              <div>
                <div className='text-2xl font-bold text-foreground'>{completedPayments.length}</div>
                <div className='text-sm text-muted-foreground'>Thành công</div>
              </div>
              <div>
                <div className='text-2xl font-bold text-foreground'>
                  {formatCurrency(totalSpent)}
                </div>
                <div className='text-sm text-muted-foreground'>Tổng chi tiêu</div>
              </div>
              <div>
                <div className='text-2xl font-bold text-foreground'>
                  {payments.filter(p => p.status === 'PENDING').length}
                </div>
                <div className='text-sm text-muted-foreground'>Đang xử lý</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
